import MomentUtils from "@date-io/moment";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Radio from "@material-ui/core/Radio";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import ClearIcon from "@material-ui/icons/Clear";
import { MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import MenuItem from "@material-ui/core/MenuItem";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PropertiesSearchDialog from "../PropertiesSearch";
import UsersSearchDialog from "../UsersSearch";
import LoadingButton from "../../Buttons/Loading";
import Divider from "../../Dividers/Dialog";
import Header from "../../Typography/Header";
import * as constants from "../../../constants";
import {
  updateShiftTemplate,
  deleteShiftTemplate,
} from "../../../utils/appsyncOperations";
import getUserLevelAvatar from "../../../utils/getUserLevelAvatar";
import * as actions from "../../../store/actions";
import { isUserRole } from "../../../utils/user";
import { init } from "../../../store/features/postOrders";
import PostOrders from "../../Lists/PostOrders";

const TemplateDetailsDialog = ({ open, onClose, templateId }) => {
  const [deletePending, setDeletePending] = React.useState(false);
  const [isEditable, setIsEditable] = React.useState(false);
  const [openShift, setOpenShift] = React.useState(false);
  const [userId, setUserId] = React.useState(null);
  const [displayName, setDisplayName] = React.useState("");
  const [levels, setLevels] = React.useState({
    one: false,
    two: false,
    three: false,
  });
  const nowMoment = moment();
  const [startTime, setStartTime] = React.useState(nowMoment);
  const timezoneGuess = momentTimezone.tz.guess(true);
  const [timezone, setTimezone] = React.useState(timezoneGuess);
  const [duration, setDuration] = useState(0);
  const [updatePending, setUpdatePending] = React.useState(false);
  const [propertiesSearchDialog, setPropertiesSearchDialog] = React.useState({
    open: false,
  });
  const [usersSearchDialog, setUsersSearchDialog] = React.useState({
    open: false,
  });

  const properties = useSelector((state) => state.properties.byId);
  const users = useSelector((state) => state.users.activeById);
  const template = useSelector((state) => state.templates.byId[templateId]);
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();

  const postOrders = useSelector((state) => state.postOrders.postOrders);

  React.useEffect(() => {
    if (open && template) {
      dispatch(
        init({ postOrders: template.postOrders ? template.postOrders : [] })
      );
      setDeletePending(false);
      setUpdatePending(false);
      setIsEditable(false);
      setOpenShift(Boolean(template.requiredLevels));
      setUserId(template.userId);
      if (!template.requiredLevels) {
        setLevels({
          one: false,
          two: false,
          three: false,
        });
      } else {
        setLevels({
          one: template.requiredLevels.includes("LEVEL_1"),
          two: template.requiredLevels.includes("LEVEL_2"),
          three: template.requiredLevels.includes("LEVEL_3"),
        });
      }
      const startMoment = moment()
        .set("hours", template.time.startHours)
        .set("minutes", template.time.startMinutes);

      setTimezone(template.timezone);
      setStartTime(startMoment);
      setDuration((template.time.duration / 3600000).toFixed(2));
      setDisplayName(template.displayName);
    } else {
      dispatch(init({ postOrders: [] }));
    }
  }, [open, template]);

  const openPropertiesSearch = () => {
    if (!updatePending && isEditable) {
      setPropertiesSearchDialog({ open: true });
    }
  };
  const closePropertiesSearch = () =>
    setPropertiesSearchDialog({ open: false });
  const openUsersSearch = () => {
    if (!openShift && !updatePending) {
      setUsersSearchDialog({ open: true });
    }
  };
  const closeUsersSearch = () => setUsersSearchDialog({ open: false });

  const user = users[userId];

  const classes = useStyles();

  const updateTemplate = async () => {
    if (!displayName)
      return enqueueSnackbar("You must enter a template name!", {
        variant: "error",
      });
    if (!duration)
      return enqueueSnackbar("You must enter a duration greater than 0!", {
        variant: "error",
      });

    let requiredLevels = [];
    let tempUserId = userId;
    if (openShift) {
      if (levels.one) requiredLevels.push("LEVEL_1");
      if (levels.two) requiredLevels.push("LEVEL_2");
      if (levels.three) requiredLevels.push("LEVEL_3");
      tempUserId = null;
    } else {
      requiredLevels = null;
    }

    try {
      setUpdatePending(true);
      const template = await updateShiftTemplate({
        displayName,
        templateId,
        time: {
          startMinutes: startTime.minutes(),
          startHours: startTime.hours(),
          duration: Math.floor(duration * 3600000), // hours to ms
        },
        userId: tempUserId,
        requiredLevels,
        timezone,
        postOrders,
      });
      dispatch(actions.templates.setTemplate(template));
      setUpdatePending(false);
      onClose();
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      setUpdatePending(false);
      console.error(e);
    }
  };

  const deleteTemplate = async () => {
    setDeletePending(true);
    try {
      const template = await deleteShiftTemplate(templateId);
      dispatch(actions.templates.deleteTemplate(template.templateId));
      setDeletePending(false);
      onClose();
    } catch (e) {
      console.error(e);
      setDeletePending(true);
      enqueueSnackbar(e, { variant: "error" });
    }
  };

  const timeZoneOptions = Object.values(constants.timezones);
  if (template && template.timezone) {
    timeZoneOptions.unshift(template.timezone);
  } else {
    timeZoneOptions.unshift(timezoneGuess);
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        {isEditable ? (
          <DialogTitle>{"Template \u2022 Editable"}</DialogTitle>
        ) : (
          <DialogTitle>{"Template"}</DialogTitle>
        )}
        <DialogContent>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Header label="Name" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant={"outlined"}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  fullWidth
                  disabled={updatePending || !isEditable}
                  label="Template Name"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Header label="Time" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={timezone}
                  onChange={(event) => setTimezone(event.target.value)}
                  disabled={!isEditable || updatePending}
                  fullWidth
                  label="Time zone"
                  required
                  select
                  SelectProps={{
                    MenuProps: {
                      style: styles.menus,
                    },
                  }}
                  variant="outlined"
                >
                  {timeZoneOptions.map((timezone) => (
                    <MenuItem key={timezone} value={timezone}>
                      {timezone}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TimePicker
                  value={startTime}
                  onChange={(moment) => setStartTime(moment)}
                  ampm={false}
                  fullWidth
                  inputVariant="outlined"
                  label="Start time"
                  disabled={updatePending || !isEditable}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  type="number"
                  value={duration !== NaN ? duration : ""}
                  disabled={updatePending || !isEditable}
                  onChange={(e) =>
                    setDuration(Math.abs(parseFloat(e.target.value)))
                  }
                  fullWidth
                  label="Duration"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Hours</InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Header label="Assignee" />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormControlLabel
                    checked={!openShift}
                    onChange={(event) => setOpenShift(!event.target.checked)}
                    control={
                      <Radio
                        color="secondary"
                        disabled={updatePending || !isEditable}
                      />
                    }
                    label="Assign to an employee"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={9}>
                <TextField
                  value={user !== undefined ? user.displayName : ""}
                  onClick={openUsersSearch}
                  disabled={openShift || updatePending || !isEditable}
                  fullWidth
                  label="Person"
                  InputProps={{
                    endAdornment:
                      user !== undefined && !openShift ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              setUserId(null);
                            }}
                            edge="end"
                            disabled={updatePending || !isEditable}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  value={
                    user !== undefined
                      ? getUserLevelAvatar(user.officerLevel)
                      : ""
                  }
                  disabled
                  fullWidth
                  label="Officer level"
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormControlLabel
                    checked={openShift}
                    onChange={(event) => setOpenShift(event.target.checked)}
                    control={
                      <Radio
                        color="secondary"
                        disabled={updatePending || !isEditable}
                      />
                    }
                    label="Create an open shift"
                  />
                </FormControl>
                <FormControl
                  className={classes.formControl}
                  component="fieldset"
                  disabled={!openShift}
                  fullWidth
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={updatePending || !isEditable || !openShift}
                          checked={levels.one}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setLevels((prevState) => ({
                              ...prevState,
                              one: checked,
                            }));
                          }}
                          value="one"
                        />
                      }
                      label="Level 1"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={updatePending || !isEditable || !openShift}
                          checked={levels.two}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setLevels((prevState) => ({
                              ...prevState,
                              two: checked,
                            }));
                          }}
                          value="two"
                        />
                      }
                      label="Level 2"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={updatePending || !isEditable || !openShift}
                          checked={levels.three}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setLevels((prevState) => ({
                              ...prevState,
                              three: checked,
                            }));
                          }}
                          value="three"
                        />
                      }
                      label="Level 3"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <PostOrders timezone={timezone} isEditable={isEditable} />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions>
          {isUserRole({ admin: true }) && (
            <>
              {isEditable ? (
                <>
                  <LoadingButton
                    onClick={deleteTemplate}
                    isLoading={deletePending}
                    color="primary"
                  >
                    Delete
                  </LoadingButton>
                  <LoadingButton
                    onClick={updateTemplate}
                    isLoading={updatePending}
                    color="primary"
                  >
                    Save
                  </LoadingButton>
                </>
              ) : (
                <Button color="primary" onClick={() => setIsEditable(true)}>
                  Edit
                </Button>
              )}
            </>
          )}
          <Button color="primary" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {open && (
        <>
          <UsersSearchDialog
            showHours={false}
            open={usersSearchDialog.open}
            onClose={closeUsersSearch}
            onUserClick={(userId) => {
              setUserId(userId);
              closeUsersSearch();
            }}
            users={Object.values(users)}
          />
        </>
      )}
    </>
  );
};

const styles = {
  menu: {
    width: 200,
  },
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

export default TemplateDetailsDialog;
